/**
 * Push tap-routing — cold-start queue logic (push-notifications-v2.md §5.3).
 *
 * A notification tap can LAUNCH the app (cold start). Capacitor delivers the
 * `pushNotificationActionPerformed` event as soon as our listener attaches —
 * but at that instant the native shell is still on its boot route ('/',
 * NativeRoot) and is ABOUT to `navigate('/today', { replace })`. Navigating to
 * the tap destination then would be clobbered by that replace. So a cold-start
 * route is QUEUED until the router has left the boot root, then flushed.
 *
 * These two helpers are the PURE part of that machinery — no React, no plugin —
 * so the queue decision is unit-testable in isolation. The mount
 * (`PushTapMount`) composes them with `useLocation` / `useAuthContext` /
 * `useNavigate`.
 *
 * A WARM tap (app already alive on a real route) is "ready" immediately, so the
 * same code path flushes with no wait.
 */

/**
 * Has the router settled enough to honor a tap route? True once auth has
 * resolved AND the shell has left the native boot root ('/').
 *
 * On native the BrowserRouter basename is '/', so a basename-stripped pathname
 * of '/' means NativeRoot (the boot splash) still owns routing and its
 * replace-navigate to /today|/welcome has not happened yet. Any other pathname
 * means boot handed off — a queued route can flush without being clobbered. A
 * warm tap observes a non-'/' pathname from the first render, so it is ready at
 * once.
 *
 * The `authLoading` clause is a defensive belt: on native, leaving '/' already
 * implies `!authLoading` (NativeRoot only routes once auth resolves), so it can
 * only ever delay a flush, never let one fire too early.
 */
export function isRouterReadyForTap(args: { authLoading: boolean; pathname: string }): boolean {
  return !args.authLoading && args.pathname !== '/';
}

/**
 * Given the current readiness and any queued route, decide the next action:
 * navigate now (and clear the queue) or keep waiting. Pure and total.
 */
export function resolveQueuedTap(args: {
  routerReady: boolean;
  queued: string | null;
}): { navigateTo: string | null; nextQueued: string | null } {
  if (args.routerReady && args.queued != null) {
    return { navigateTo: args.queued, nextQueued: null };
  }
  return { navigateTo: null, nextQueued: args.queued };
}
